import {
  SANDSTAR_PROVIDER,
  SMART_CAFE_PARTNERID,
} from '@/constant/global/global.constant';
import {
  VDI_TYPES,
  VDI_VERSION,
} from '@/constant/gatewayTransaction/gatewayTransaction.constant';
import { getDateTime } from '@/utils/dateTime';
import { APP_VDI } from '@/constant/theme/colors.constant';
import {
  IVDITransactionErrorProps,
  IVDITransactionProps,
  Meta,
  NewMeta,
} from '@/utils/types/vdiTransaction';
import _ from 'lodash';
import { IVdiVersion } from '@/services/vdiTransaction/vdiTransaction.type';
import { IProvider } from '@/services/provider/provider.type';
import { IAppConfigs, IVdiConfigs } from './vdiTransaction.interface';

export const DEFAULT_AUTO_REFRESH_INTERVAL = 30; // 30 seconds
export const MAX_REFRESH_COUNT = 10; // 10 times
export const VDI_TRANSACTION_SUCCESS_CODE = 0;
export const VDI_RECONCILIATION_SUCCESS_CODE = 100;

export const VDI_TRANSACTION_LIMIT = 100;
export const GATEWAY_TRANSACTION_PAGE = 1;
export const GATEWAY_APPLICATION_ID = 'VDI_APPLICATION_ID';
export const GATEWAYVERSION = 'gatewayVersion';
export const SEEDPROVIDER = 'CANTALOUPE';
export const VDIV1 = 'VDIv1';
export const VDIV2 = 'VDIv2';
export const SANDSTAR = 'SANDSTAR';
export const SEED = 'SEED';
export const RECONCILIATION_KEY = 'reconciliation';

type Provider = {
  providerId: string;
  partnerId: string;
  name: string;
};

type VdiTransaction = {
  vdiTransactionId: string;
  providerId: string;
  partnerId: string;
  vdiVersion: string;
  operatorId: string;
  correlationId: string;
  type: string;
  transactionDtm: string; // ISO 8601 Date string
  accepted: boolean;
  statusCode: number;
  processedOn: string; // ISO 8601 Date string
  sentOn: string; // ISO 8601 Date string
  applicationId: string;
  internalSentOn: string; // ISO 8601 Date string
  internalProcessedOn: string; // ISO 8601 Date string
  createdOn: string; // ISO 8601 Date string
  updatedOn: string; // ISO 8601 Date string
  provider: Provider;
  errors: string[]; // Array of error strings
};

export const STEPPER_HOVER_CONTENT = {
  FAILED_AT_ACCEPTANCE_STEP: 'Failed at VdiV1/VdiV2 Acceptance',
  FAILED_DURING_PROCESS_AT_GATEWAY_STEP: 'Failed during processing at Gateway',
  FAILED_DURING_PROCESS_AT_PROCESSING_STEP: 'Failed during processing',
  FAILED_DURING_PROCESS_AT_PARTNER_STEP: 'Failed during processing at Partner',
  FAILED_DURING_PROCESS_AT_CLIENT_STEP:
    'Failed during process at Client  (SEN)',
};
export const STEPPER_FAILURE_MESSAGES = {
  ACCEPTANCE_LEVEL: 'Request acceptance is pending.',
  GATEWAY_LEVEL: 'Yet to forward to Hub.',
  PROCESSING_LEVEL: 'Processing is still pending.',
  PARTNER_LEVEL: 'Yet to sync with Partner.',
  CLIENT_LEVEL: 'Yet to sync with Client.',
};

export const VDI_EXPANDED_FAILURE_MESSAGES = {
  DETAIL: 'Failed to load transaction details. Please try again.',
  PROVIDER: 'Failed to load provider details. Please try again.',
};

export const EVDIRequestProcessStatus = {
  SUCCESS: 'Success',
  FAILED: 'Failed',
  INPROGRESS: 'In Progress',
};

export enum EVDI_REQUEST_STATUS {
  ALL = 'ALL',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  PROCESS_FAILED = 'PROCESS_FAILED',
  PARTNER_FAILED = 'PARTNER_FAILED',
  CLIENT_FAILED = 'CLIENT_FAILED',
  RETRY_PARTNER_SUCCESS = 'RETRY_PARTNER_SUCCESS',
  RETRY_CLIENT_SUCCESS = 'RETRY_CLIENT_SUCCESS',
  RETRY_PARTNER_FAILED = 'RETRY_PARTNER_FAILED',
  RETRY_CLIENT_FAILED = 'RETRY_CLIENT_FAILED',
  SERVER_ERROR_5XX = 'SERVER_ERROR_5XX',
  CLIENT_GET_STATE_SUCCESS = 'CLIENT_GET_STATE_SUCCESS',
  CLIENT_GET_STATE_FAILED = 'CLIENT_GET_STATE_FAILED',
  RETRY_CLIENT_GET_STATE_SUCCESS = 'RETRY_CLIENT_GET_STATE_SUCCESS',
  RETRY_CLIENT_GET_STATE_FAILED = 'RETRY_CLIENT_GET_STATE_FAILED',
}

export enum EVdiVersion {
  VDIv1 = '1',
  VDIv2 = '1.1',
}

export enum EVDITypes {
  MMS_MARKETS = 'mmsMarkets',
  MMS_PRODUCTS = 'mmsProducts',
  MMS_DEVICES = 'mmsDevices',
  MMS_SALES = 'mmsSales',
  VDI_PERIOD_TOTALS = 'vdiPeriodTotals',
  VDI_PERIOD_SALES_TRANSACTIONS = 'vdiPeriodSalesTransactions',
  VDI_PRODUCT_SALE_TRANSACTION_DETAILS = 'vdiProductSaleTransactionDetails',
}

export const VDI_5XX_RETRY_ERRORCODES = [
  { code: 500, message: 'Internal Server Error' },
  { code: 501, message: 'Not Implemented' },
  { code: 502, message: 'Bad Gateway' },
  { code: 503, message: 'Service Unavailable' },
  { code: 504, message: 'Gateway Timeout' },
  { code: 505, message: 'HTTP Version Not Supported' },
  { code: 506, message: 'Variant Also Negotiates' },
  { code: 507, message: 'Insufficient Storage' },
  { code: 508, message: 'Loop Detected' },
  { code: 510, message: 'Not Extended' },
  { code: 511, message: 'Network Authentication Required' },
];

export const VDI_PARTNER_CONFIG = {
  CLIENT_AND_PARTNER: {
    checkpoints: [
      {
        path: 'accepted',
        success: false,
        versions: [EVdiVersion.VDIv1, EVdiVersion.VDIv2],
        types: Object.values(EVDITypes),
        failWeight: -5,
      },
      {
        path: 'internalSentOn',
        success: false,
        versions: [EVdiVersion.VDIv1, EVdiVersion.VDIv2],
        types: Object.values(EVDITypes),
        failWeight: -5,
      },
      {
        path: 'internalProcessedOn',
        success: false,
        versions: [EVdiVersion.VDIv1, EVdiVersion.VDIv2],
        types: Object.values(EVDITypes),
        failWeight: -5,
      },
      {
        path: 'processedOn',
        success: false,
        versions: [EVdiVersion.VDIv1, EVdiVersion.VDIv2],
        types: [
          EVDITypes.MMS_MARKETS,
          EVDITypes.MMS_PRODUCTS,
          EVDITypes.MMS_DEVICES,
          EVDITypes.MMS_SALES,
        ],
        failWeight: -5,
      },
      {
        path: 'sentOn',
        success: false,
        versions: [EVdiVersion.VDIv1, EVdiVersion.VDIv2],
        types: [
          EVDITypes.MMS_MARKETS,
          EVDITypes.MMS_PRODUCTS,
          EVDITypes.MMS_DEVICES,
          EVDITypes.MMS_SALES,
        ],
        failWeight: -5,
      },
      {
        path: 'meta.client.get_state.success',
        success: false,
        versions: [EVdiVersion.VDIv2],
        types: [
          EVDITypes.MMS_MARKETS,
          EVDITypes.MMS_PRODUCTS,
          EVDITypes.MMS_DEVICES,
          EVDITypes.MMS_SALES,
        ],
        failWeight: -5,
      },
    ],
  },
  ONLY_CLIENT: {
    checkpoints: [
      {
        path: 'accepted',
        success: false,
        versions: [EVdiVersion.VDIv2],
        types: Object.values(EVDITypes),
        failWeight: -5,
      },
      {
        path: 'internalSentOn',
        success: false,
        versions: [EVdiVersion.VDIv2],
        types: Object.values(EVDITypes),
        failWeight: -5,
      },
      {
        path: 'internalProcessedOn',
        success: false,
        versions: [EVdiVersion.VDIv2],
        types: Object.values(EVDITypes),
        failWeight: -5,
      },
      {
        path: 'processedOn',
        success: false,
        versions: [EVdiVersion.VDIv2],
        types: [
          EVDITypes.MMS_MARKETS,
          EVDITypes.MMS_PRODUCTS,
          EVDITypes.MMS_DEVICES,
          EVDITypes.MMS_SALES,
        ],
        failWeight: -5,
      },
      {
        path: 'sentOn',
        success: false,
        versions: [EVdiVersion.VDIv2],
        types: [
          EVDITypes.MMS_MARKETS,
          EVDITypes.MMS_PRODUCTS,
          EVDITypes.MMS_DEVICES,
          EVDITypes.MMS_SALES,
        ],
        failWeight: -5,
      },
      {
        path: 'meta.vdi_get_state.client',
        success: false,
        versions: [EVdiVersion.VDIv2],
        types: [
          EVDITypes.MMS_MARKETS,
          EVDITypes.MMS_PRODUCTS,
          EVDITypes.MMS_DEVICES,
          EVDITypes.MMS_SALES,
        ],
        failWeight: -5,
      },
    ],
  },
  ONLY_PARTNER: {
    checkpoints: [
      {
        path: 'accepted',
        success: false,
        versions: [EVdiVersion.VDIv1, EVdiVersion.VDIv2],
        types: Object.values(EVDITypes),
        failWeight: -5,
      },
      {
        path: 'internalSentOn',
        success: false,
        versions: [EVdiVersion.VDIv1, EVdiVersion.VDIv2],
        types: Object.values(EVDITypes),
        failWeight: -5,
      },
      {
        path: 'internalProcessedOn',
        success: false,
        versions: [EVdiVersion.VDIv1, EVdiVersion.VDIv2],
        types: Object.values(EVDITypes),
        failWeight: -5,
      },
      {
        path: 'processedOn',
        success: false,
        versions: [EVdiVersion.VDIv1, EVdiVersion.VDIv2],
        types: [
          EVDITypes.MMS_MARKETS,
          EVDITypes.MMS_PRODUCTS,
          EVDITypes.MMS_DEVICES,
          EVDITypes.MMS_SALES,
        ],
        failWeight: -5,
      },
      // {
      //   path: 'sentOn',
      //   success: false,
      //   versions: [EVdiVersion.VDIv1, EVdiVersion.VDIv2],
      //   types: [
      //     EVDITypes.MMS_MARKETS,
      //     EVDITypes.MMS_PRODUCTS,
      //     EVDITypes.MMS_DEVICES,
      //     EVDITypes.MMS_SALES
      //   ],
      //   failWeight: -5
      // },
      // {
      //   path: 'meta.vdi_get_state.client',
      //   success: false,
      //   versions: [EVdiVersion.VDIv2],
      //   types: [
      //     EVDITypes.MMS_MARKETS,
      //     EVDITypes.MMS_PRODUCTS,
      //     EVDITypes.MMS_DEVICES,
      //     EVDITypes.MMS_SALES
      //   ],
      //   failWeight: -5
      // }
    ],
  },
  RECONCILIATION: {
    checkpoints: [
      {
        path: 'accepted',
        success: false,
        versions: [EVdiVersion.VDIv1, EVdiVersion.VDIv2],
        types: Object.values(EVDITypes),
        failWeight: -5,
      },
      {
        path: 'internalSentOn',
        success: false,
        versions: [EVdiVersion.VDIv1, EVdiVersion.VDIv2],
        types: Object.values(EVDITypes),
        failWeight: -5,
      },
      {
        path: 'internalProcessedOn',
        success: false,
        versions: [EVdiVersion.VDIv1, EVdiVersion.VDIv2],
        types: Object.values(EVDITypes),
        failWeight: -5,
      },
    ],
  },
};

export const getProviderConfigBasedOnPartnerId = (
  partnerId: string,
  providersData: IProvider[],
) => {
  const providerConfig = providersData?.find(
    (provider) => provider?.partnerId === partnerId,
  )?.configs;
  return providerConfig || null;
};

export const getStepperSteps = (configsData: IAppConfigs) => {
  let stepperSteps = {
    hasPartnerStep: false,
    hasClientStep: false,
    hasReconciliation: false,
  };
  if (!configsData || _.isEmpty(configsData)) {
    return stepperSteps;
  }
  const { VDI_SETTINGS, RECONCILIATION } = configsData;
  if (VDI_SETTINGS?.CLIENTS?.ENABLED) {
    const hasClientStep =
      VDI_SETTINGS?.CLIENTS?.VDI_TYPES &&
      VDI_SETTINGS?.CLIENTS?.VDI_TYPES.length > 0;
    stepperSteps = { ...stepperSteps, hasClientStep };
  }
  if (VDI_SETTINGS?.VMS?.ENABLED && VDI_SETTINGS?.MMS?.ENABLED) {
    const hasPartnerStep =
      VDI_SETTINGS?.VMS?.ENABLED && VDI_SETTINGS?.MMS?.ENABLED;
    stepperSteps = { ...stepperSteps, hasPartnerStep };
  }
  if (RECONCILIATION?.ENABLED) {
    const hasReconciliation = RECONCILIATION?.ENABLED;
    stepperSteps = { ...stepperSteps, hasReconciliation };
  }
  return stepperSteps;
};
export const getVDIPartnerConfigKeysBasedOnSteps = (
  hasPartner: boolean,
  hasClient: boolean,
  hasReconciliation: boolean,
) => {
  if (hasPartner && hasClient) {
    return 'CLIENT_AND_PARTNER';
  }
  if (hasClient && !hasPartner) {
    return 'ONLY_CLIENT';
  }
  if (hasPartner && !hasClient) {
    return 'ONLY_PARTNER';
  }
  if (hasReconciliation) {
    return 'RECONCILIATION';
  }

  return 'NO_CONFIGURATION';
};
export const transactionStatusFilters = [
  {
    queryParam: EVDI_REQUEST_STATUS.ALL,
    name: 'All Status',
    support: VDI_VERSION,
  },
  {
    queryParam: EVDI_REQUEST_STATUS.SUCCESS,
    name: 'Success',
    support: VDI_VERSION,
  },
  {
    queryParam: EVDI_REQUEST_STATUS.FAILED,
    name: 'Failed',
    support: VDI_VERSION,
  },
  {
    queryParam: EVDI_REQUEST_STATUS.PROCESS_FAILED,
    name: 'Process Failed',
    support: VDI_VERSION,
  },
  {
    queryParam: EVDI_REQUEST_STATUS.PARTNER_FAILED,
    name: 'Partner Failed',
    support: VDI_VERSION,
  },
  {
    queryParam: EVDI_REQUEST_STATUS.CLIENT_FAILED,
    name: 'Client Failed',
    support: VDI_VERSION,
  },
  {
    queryParam: EVDI_REQUEST_STATUS.RETRY_PARTNER_SUCCESS,
    name: 'Retry Partner Success',
    support: VDI_VERSION,
  },
  {
    queryParam: EVDI_REQUEST_STATUS.RETRY_CLIENT_SUCCESS,
    name: 'Retry Client Success',
    support: VDI_VERSION,
  },
  {
    queryParam: EVDI_REQUEST_STATUS.RETRY_PARTNER_FAILED,
    name: 'Retry Partner Failed',
    support: [VDI_VERSION[1]],
  },
  {
    queryParam: EVDI_REQUEST_STATUS.RETRY_CLIENT_FAILED,
    name: 'Retry Client Failed',
    support: [VDI_VERSION[1]],
  },
  {
    queryParam: EVDI_REQUEST_STATUS.SERVER_ERROR_5XX,
    name: 'Server Error 5XX',
    support: VDI_VERSION,
  },
  {
    queryParam: EVDI_REQUEST_STATUS.CLIENT_GET_STATE_SUCCESS,
    name: 'Client Get State Success',
    support: [VDI_VERSION[1]],
  },
  {
    queryParam: EVDI_REQUEST_STATUS.CLIENT_GET_STATE_FAILED,
    name: 'Client Get State Failed',
    support: [VDI_VERSION[1]],
  },
  {
    queryParam: EVDI_REQUEST_STATUS.RETRY_CLIENT_GET_STATE_SUCCESS,
    name: 'Retry Client Get State Success',
    support: [VDI_VERSION[1]],
  },
  {
    queryParam: EVDI_REQUEST_STATUS.RETRY_CLIENT_GET_STATE_FAILED,
    name: 'Retry Client Get State Failed',
    support: [VDI_VERSION[1]],
  },
];

export enum SMARTCAFE_EVDI_REQUEST_STATUS {
  ALL = 'ALL',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  PROCESS_FAILED = 'PROCESS_FAILED',
  PARTNER_FAILED = 'PARTNER_FAILED',
  SERVER_ERROR_5XX = 'SERVER_ERROR_5XX',
}

export const smartCafetransactionStatusFilters = [
  {
    queryParam: SMARTCAFE_EVDI_REQUEST_STATUS.ALL,
    name: 'All Status',
    support: VDI_VERSION,
  },
  {
    queryParam: SMARTCAFE_EVDI_REQUEST_STATUS.SUCCESS,
    name: 'Success',
    support: VDI_VERSION,
  },
  {
    queryParam: SMARTCAFE_EVDI_REQUEST_STATUS.FAILED,
    name: 'Failed',
    support: VDI_VERSION,
  },
  {
    queryParam: SMARTCAFE_EVDI_REQUEST_STATUS.PROCESS_FAILED,
    name: 'Process Failed',
    support: VDI_VERSION,
  },
  {
    queryParam: SMARTCAFE_EVDI_REQUEST_STATUS.PARTNER_FAILED,
    name: 'Partner Failed',
    support: VDI_VERSION,
  },
  {
    queryParam: SMARTCAFE_EVDI_REQUEST_STATUS.SERVER_ERROR_5XX,
    name: 'Server Error 5XX',
    support: VDI_VERSION,
  },
];
export const getVDITransactionStatusFilters = (
  selectedVersions: IVdiVersion[],
) => {
  if (!selectedVersions || selectedVersions.length === 0) return [];

  return transactionStatusFilters.filter((filter) =>
    filter.support.some((supportedVersion) =>
      selectedVersions.some(
        (selectedVersion) => selectedVersion?.id === supportedVersion?.id,
      ),
    ),
  );
};
export const getLabelBasedOnProviderConfiguration = (
  VdiSettingsConfigData: IVdiConfigs,
  vdiTypeFromVdiTransactionItem: string,
) => {
  const stepperLabels = {
    PARTNER_FLOW_LABEL: '',
    CLIENT_FLOW_LABEL: '',
    ACCEPTANCE_STEP_SERVER_LABEL: '',
    PROCESSING_STEP_SERVER_LABEL: '',
  };
  if (
    VdiSettingsConfigData?.MMS?.ENABLED &&
    (VdiSettingsConfigData?.MMS?.VDI_TYPES).includes(
      vdiTypeFromVdiTransactionItem,
    )
  ) {
    stepperLabels.PARTNER_FLOW_LABEL =
      (VdiSettingsConfigData?.MMS?.AUTH.AUTH_TYPE).toUpperCase();
    if (!_.isEmpty(VdiSettingsConfigData?.MMS?.VERSION)) {
      stepperLabels.PROCESSING_STEP_SERVER_LABEL = `VDIv${VdiSettingsConfigData?.MMS?.VERSION}`;
    }
  } else if (
    VdiSettingsConfigData?.VMS?.ENABLED &&
    (VdiSettingsConfigData?.VMS?.VDI_TYPES).includes(
      vdiTypeFromVdiTransactionItem,
    )
  ) {
    stepperLabels.PARTNER_FLOW_LABEL =
      (VdiSettingsConfigData?.VMS?.AUTH.AUTH_TYPE).toUpperCase();
    if (!_.isEmpty(VdiSettingsConfigData?.VMS?.VERSION)) {
      stepperLabels.PROCESSING_STEP_SERVER_LABEL = `VDIv${VdiSettingsConfigData?.VMS?.VERSION}`;
    }
  }
  if (
    VdiSettingsConfigData?.CLIENTS?.ENABLED &&
    (VdiSettingsConfigData?.CLIENTS?.VDI_TYPES).includes(
      vdiTypeFromVdiTransactionItem,
    )
  ) {
    stepperLabels.CLIENT_FLOW_LABEL =
      (VdiSettingsConfigData?.CLIENTS?.AUTH.AUTH_TYPE).toUpperCase();
  }
  if (
    vdiTypeFromVdiTransactionItem === VDI_TYPES[3].id ||
    vdiTypeFromVdiTransactionItem === VDI_TYPES[2].id
  ) {
    // mmsProducts mmsMarkets
    stepperLabels.ACCEPTANCE_STEP_SERVER_LABEL = `VDIv${VdiSettingsConfigData?.VMS?.VERSION}`;
  } else if (
    vdiTypeFromVdiTransactionItem === VDI_TYPES[4].id ||
    vdiTypeFromVdiTransactionItem === VDI_TYPES[1].id
  ) {
    // Mms sales , mms Devices
    stepperLabels.ACCEPTANCE_STEP_SERVER_LABEL = `VDIv${VdiSettingsConfigData?.MMS?.VERSION}`;
  }
  return stepperLabels;
};

export const getStatusOfEachStep = (
  vdiTransactionData: IVDITransactionProps,
  providerListData: IProvider[],
) => {
  const providerConfigurationData = getProviderConfigBasedOnPartnerId(
    vdiTransactionData?.partnerId,
    providerListData,
  );
  const stepperSteps = getStepperSteps(providerConfigurationData);
  const { hasPartnerStep, hasClientStep } = stepperSteps;
  const { VDI_SETTINGS } = providerConfigurationData || {};
  const labelBasedOnProviderConfiguration =
    getLabelBasedOnProviderConfiguration(
      VDI_SETTINGS,
      vdiTransactionData?.type,
    );
  const providerWithOnlyClientFlow = hasClientStep && !hasPartnerStep; // Sandstar with only client flow
  const providerWithOnlyPartnerFlow = hasPartnerStep && !hasClientStep; // SmartCafe with only partner flow
  const sandStar =
    vdiTransactionData?.provider?.partnerId === SANDSTAR_PROVIDER.PARTNERID &&
    vdiTransactionData?.provider?.name === SANDSTAR_PROVIDER.NAME;
  const isSeedProvider = vdiTransactionData?.provider?.name === SEEDPROVIDER;
  const isSeedWithMMPMMM =
    isSeedProvider &&
    (vdiTransactionData?.type === VDI_TYPES[3].id ||
      vdiTransactionData?.type === VDI_TYPES[2].id);
  const isSeedWithMMSMMD =
    isSeedProvider &&
    (vdiTransactionData?.type === VDI_TYPES[4].id ||
      vdiTransactionData?.type === VDI_TYPES[1].id);

  const getServerLabel = (step: number): string => {
    if (step === 0)
      return (
        `${isSeedWithMMPMMM ? VDIV1 : ''}${isSeedWithMMSMMD ? VDIV2 : ''}${sandStar ? VDIV2 : ''}` ||
        VDIV2
      );
    if (step === 2)
      return (
        `${isSeedWithMMPMMM ? VDIV2 : ''}${isSeedWithMMSMMD ? VDIV1 : ''}${sandStar ? VDIV2 : ''}` ||
        VDIV2
      );
    return VDIV2;
  };
  const label = `${sandStar ? '(SEN)' : ''}${isSeedWithMMPMMM ? SANDSTAR : ''}${isSeedWithMMSMMD ? SEED : ''}`;

  const getHoverAndDescriptionContentBasedOnStatus = (
    vdiTransactionData: IVDITransactionProps,
  ) => {
    const ACCEPTANCE_STEP_CONTENT = {
      HOVER_CONTENT: {
        success: `Internal Status Code : ${vdiTransactionData?.internalStatusCode}`,
        failure: STEPPER_HOVER_CONTENT.FAILED_AT_ACCEPTANCE_STEP,
        active: 'Awaiting acceptance',
      },
      DESCRIPTION: {
        success: `${getDateTime(vdiTransactionData?.createdOn)} (${labelBasedOnProviderConfiguration.ACCEPTANCE_STEP_SERVER_LABEL}) `,
        failure: STEPPER_FAILURE_MESSAGES.ACCEPTANCE_LEVEL,
      },
    };
    const GATEWAY_STEP_CONTENT = {
      HOVER_CONTENT: {
        success: `Internal Status Code : ${vdiTransactionData?.internalStatusCode}`,
        failure: STEPPER_HOVER_CONTENT.FAILED_DURING_PROCESS_AT_GATEWAY_STEP,
        active: 'Forwarding to Gateway',
      },
      DESCRIPTION: {
        success: `${getDateTime(vdiTransactionData?.internalSentOn)}`,
        failure: STEPPER_FAILURE_MESSAGES.GATEWAY_LEVEL,
      },
    };
    const PROCESSING_STEP_CONTENT = {
      HOVER_CONTENT: {
        success: `Internal Status Code : ${vdiTransactionData?.internalStatusCode}`,
        failure: STEPPER_HOVER_CONTENT.FAILED_DURING_PROCESS_AT_GATEWAY_STEP,
        active: 'Processing',
      },
      DESCRIPTION: {
        success: `${getDateTime(vdiTransactionData?.internalProcessedOn)} (${labelBasedOnProviderConfiguration?.PROCESSING_STEP_SERVER_LABEL})`, // Showing same description for both success and failure scenarios
        failure: `${getDateTime(vdiTransactionData?.internalProcessedOn)} (${labelBasedOnProviderConfiguration?.PROCESSING_STEP_SERVER_LABEL})`,
      },
    };
    const SYNC_TO_PARTNER_STEP_CONTENT = {
      HOVER_CONTENT: {
        success: `Status Code : ${vdiTransactionData?.statusCode}`,
        failure: `${STEPPER_HOVER_CONTENT.FAILED_DURING_PROCESS_AT_PARTNER_STEP} (${labelBasedOnProviderConfiguration?.PARTNER_FLOW_LABEL})`,
        active: 'Syncing with Partner',
      },
      DESCRIPTION: {
        success: `${getDateTime(vdiTransactionData?.processedOn)} (${labelBasedOnProviderConfiguration?.PARTNER_FLOW_LABEL}).`,
        failure: STEPPER_FAILURE_MESSAGES.PARTNER_LEVEL,
      },
    };
    const SYNC_TO_CLIENT_STEP_CONTENT = {
      HOVER_CONTENT: {
        success: `Status Code : ${vdiTransactionData?.statusCode}`,
        failure: `${STEPPER_HOVER_CONTENT.FAILED_DURING_PROCESS_AT_CLIENT_STEP}`,
        active: 'Syncing with Client',
      },
      DESCRIPTION: {
        success: `${getDateTime(vdiTransactionData?.sentOn)} (SEN).`,
        failure: `${STEPPER_FAILURE_MESSAGES.CLIENT_LEVEL} (SEN).`,
      },
    };
    return {
      ACCEPTANCE_STEP_CONTENT,
      GATEWAY_STEP_CONTENT,
      PROCESSING_STEP_CONTENT,
      SYNC_TO_PARTNER_STEP_CONTENT,
      SYNC_TO_CLIENT_STEP_CONTENT,
    };
  };
  // Type guards
  function isNewMeta(meta: Meta | NewMeta): meta is NewMeta {
    return (meta as NewMeta).client !== undefined;
  }

  function isMeta(meta: Meta | NewMeta): meta is Meta {
    return (meta as Meta).vdi_get_state !== undefined;
  }
  const getIconBgColor = (
    IsSuccessUpToCurrentStep: boolean,
    isSuccessUpToPreviousStep: boolean,
    isNotCompleted: boolean,
    isActive: boolean,
  ) => {
    const isSuccess = IsSuccessUpToCurrentStep;
    const isStepFailed = !IsSuccessUpToCurrentStep && isSuccessUpToPreviousStep;
    return isSuccess
      ? APP_VDI.doneBg
      : (isNotCompleted && !isStepFailed) || isActive
        ? APP_VDI.inProgressBg
        : APP_VDI.failedBg;
  };
  const {
    internalSentOn,
    accepted,
    internalStatusCode,
    internalProcessedOn,
    statusCode,
    processedOn,
    sentOn,
    meta,
  } = vdiTransactionData;

  const isRetryable5XX = (code: number): boolean => {
    return VDI_5XX_RETRY_ERRORCODES.some((data) => data.code === code);
  };
  const isPartner5xxFailure =
    isRetryable5XX(statusCode) &&
    (processedOn === null || processedOn !== null) &&
    _.isUndefined(sentOn);
  const isClient5xxFailure =
    !_.isNil(processedOn) &&
    !isPartner5xxFailure &&
    isRetryable5XX(statusCode) &&
    (sentOn === null || sentOn !== null);

  const isNewDataStructure = !!Object.keys(meta).find(
    (key) => key === RECONCILIATION_KEY,
  );
  const isAcceptanceStepSuccessful = accepted === true;
  const isGatewayStepSuccessful = !_.isNil(internalSentOn);
  const isProcessedStepSuccessful =
    !_.isNil(internalProcessedOn) &&
    internalStatusCode === VDI_TRANSACTION_SUCCESS_CODE;
  const isPartnerStepSuccessful =
    (!isPartner5xxFailure && (sentOn === null || !_.isNil(sentOn))) ||
    (!_.isNil(processedOn) && statusCode === VDI_TRANSACTION_SUCCESS_CODE);
  const isClientStepSuccessfulNew =
    (!_.isNil(sentOn) && statusCode === VDI_TRANSACTION_SUCCESS_CODE) ||
    (!_.isNil(sentOn) &&
      isNewMeta(meta) &&
      !_.isUndefined(meta.client?.get_state));

  const isClientStepSuccessfulOld =
    (!_.isNil(sentOn) && statusCode === VDI_TRANSACTION_SUCCESS_CODE) ||
    (!_.isNil(sentOn) &&
      isMeta(meta) &&
      !_.isUndefined(meta.vdi_get_state) &&
      !isClient5xxFailure);

  const isClientStepSuccessful = isNewDataStructure
    ? isClientStepSuccessfulNew
    : isClientStepSuccessfulOld;

  const IsSuccessUptoAcceptanceStep = isAcceptanceStepSuccessful;
  const IsSuccessUptoGatewayStep =
    isGatewayStepSuccessful && IsSuccessUptoAcceptanceStep;
  const IsSuccessUptoProcessingStep =
    isProcessedStepSuccessful && IsSuccessUptoGatewayStep;
  const IsSuccessUptoPartnerStep =
    isPartnerStepSuccessful &&
    IsSuccessUptoProcessingStep &&
    !isPartner5xxFailure;
  const IsSuccessUptoClientStep =
    (providerWithOnlyClientFlow
      ? isClientStepSuccessful && IsSuccessUptoProcessingStep
      : isClientStepSuccessful && IsSuccessUptoPartnerStep) &&
    (isNewDataStructure ? true : !isClient5xxFailure);

  const isAcceptanceStepActive =
    !isAcceptanceStepSuccessful && _.isNil(internalSentOn);
  const isGatewayStepActive =
    IsSuccessUptoAcceptanceStep &&
    internalStatusCode === VDI_TRANSACTION_SUCCESS_CODE &&
    _.isNil(internalSentOn);
  const isProcessingStepActive =
    IsSuccessUptoGatewayStep &&
    internalStatusCode === VDI_TRANSACTION_SUCCESS_CODE &&
    _.isNil(internalProcessedOn);
  const isPartnerStepActive =
    IsSuccessUptoProcessingStep &&
    _.isUndefined(processedOn) &&
    statusCode !== VDI_TRANSACTION_SUCCESS_CODE;
  const isClientStepActive =
    (providerWithOnlyClientFlow
      ? IsSuccessUptoProcessingStep
      : IsSuccessUptoPartnerStep) &&
    _.isNil(sentOn) &&
    statusCode === VDI_TRANSACTION_SUCCESS_CODE &&
    (isClient5xxFailure || !isClientStepSuccessful);

  const STEP_STATUS_DATA = {
    acceptanceStepStatus: {
      success: isAcceptanceStepSuccessful,
      active: isAcceptanceStepActive,
      notCompleted: !isAcceptanceStepSuccessful || !IsSuccessUptoAcceptanceStep,
    },
    gatewayStepStatus: {
      success: isGatewayStepSuccessful,
      active: isGatewayStepActive,
      notCompleted: !isGatewayStepSuccessful || !IsSuccessUptoGatewayStep,
    },

    processingStepStatus: {
      success: isProcessedStepSuccessful,
      active: isProcessingStepActive,
      notCompleted: isProcessedStepSuccessful || !IsSuccessUptoProcessingStep,
    },
    syncToPartnerStepStatus: {
      success: isPartnerStepSuccessful,
      active: isPartnerStepActive && !isPartner5xxFailure,
      notCompleted:
        (isPartnerStepSuccessful || !IsSuccessUptoPartnerStep) &&
        (isPartner5xxFailure || !IsSuccessUptoPartnerStep),
    },
    syncToClientStepStatus: {
      success: isClientStepSuccessful,
      active: isClientStepActive && !isClient5xxFailure,
      notCompleted:
        (providerWithOnlyClientFlow
          ? isClientStepSuccessful || !IsSuccessUptoProcessingStep
          : isClientStepSuccessful || !IsSuccessUptoPartnerStep) &&
        !isClient5xxFailure,
    },
  };
  const {
    acceptanceStepStatus,
    gatewayStepStatus,
    processingStepStatus,
    syncToPartnerStepStatus,
    syncToClientStepStatus,
  } = STEP_STATUS_DATA;
  const {
    ACCEPTANCE_STEP_CONTENT,
    GATEWAY_STEP_CONTENT,
    PROCESSING_STEP_CONTENT,
    SYNC_TO_PARTNER_STEP_CONTENT,
    SYNC_TO_CLIENT_STEP_CONTENT,
  } = getHoverAndDescriptionContentBasedOnStatus(vdiTransactionData);

  const ALL_STEPS_COMPLETE_DETAILS = {
    ACCEPTANCE: {
      currentStepStatus: isAcceptanceStepSuccessful,
      nextStepStatus: isGatewayStepSuccessful,
      previousStepStatus: true,
      description: acceptanceStepStatus.success
        ? ACCEPTANCE_STEP_CONTENT.DESCRIPTION.success
        : ACCEPTANCE_STEP_CONTENT.DESCRIPTION.failure,
      hoverContent: isAcceptanceStepActive
        ? ACCEPTANCE_STEP_CONTENT.HOVER_CONTENT.active
        : isAcceptanceStepSuccessful
          ? ACCEPTANCE_STEP_CONTENT.HOVER_CONTENT.success
          : ACCEPTANCE_STEP_CONTENT.HOVER_CONTENT.failure,

      iconBackgroundColor: getIconBgColor(
        IsSuccessUptoAcceptanceStep,
        IsSuccessUptoAcceptanceStep,
        acceptanceStepStatus.notCompleted,
        acceptanceStepStatus.active,
      ),
    },
    GATEWAY: {
      currentStepStatus: isGatewayStepSuccessful,
      nextStepStatus: isProcessedStepSuccessful,
      previousStepStatus: isAcceptanceStepSuccessful,
      description: gatewayStepStatus.success
        ? GATEWAY_STEP_CONTENT.DESCRIPTION.success
        : GATEWAY_STEP_CONTENT.DESCRIPTION.failure,
      hoverContent: isGatewayStepActive
        ? GATEWAY_STEP_CONTENT.HOVER_CONTENT.active
        : isGatewayStepSuccessful
          ? GATEWAY_STEP_CONTENT.HOVER_CONTENT.success
          : !IsSuccessUptoAcceptanceStep
            ? ''
            : GATEWAY_STEP_CONTENT.HOVER_CONTENT.failure,
      iconBackgroundColor: getIconBgColor(
        IsSuccessUptoGatewayStep,
        IsSuccessUptoAcceptanceStep,
        gatewayStepStatus.notCompleted,
        gatewayStepStatus.active,
      ),
    },
    PROCESSED: {
      currentStepStatus: isProcessedStepSuccessful,
      nextStepStatus: providerWithOnlyClientFlow
        ? isClientStepSuccessful
        : isPartnerStepSuccessful, // Sandstar doesn't have partner flow, so checking if client step is successful
      previousStepStatus: isGatewayStepSuccessful,
      description: processingStepStatus.success
        ? PROCESSING_STEP_CONTENT.DESCRIPTION.success
        : PROCESSING_STEP_CONTENT.DESCRIPTION.failure,
      hoverContent: isProcessingStepActive
        ? PROCESSING_STEP_CONTENT.HOVER_CONTENT.active
        : isProcessedStepSuccessful
          ? PROCESSING_STEP_CONTENT.HOVER_CONTENT.success
          : IsSuccessUptoGatewayStep
            ? PROCESSING_STEP_CONTENT.HOVER_CONTENT.failure
            : '',
      iconBackgroundColor: getIconBgColor(
        IsSuccessUptoProcessingStep,
        IsSuccessUptoGatewayStep,
        processingStepStatus.notCompleted,
        processingStepStatus.active,
      ),
    },
    SYNC_TO_PARTNER: {
      currentStepStatus: isPartnerStepSuccessful,
      nextStepStatus: providerWithOnlyClientFlow
        ? isClientStepSuccessful
        : isPartnerStepSuccessful, // Sandstar doesn't have partner flow, so checking if client step is successful
      previousStepStatus: isProcessedStepSuccessful,
      description:
        syncToPartnerStepStatus.success || !_.isNil(processedOn)
          ? SYNC_TO_PARTNER_STEP_CONTENT.DESCRIPTION.success
          : SYNC_TO_PARTNER_STEP_CONTENT.DESCRIPTION.failure,
      hoverContent: isPartnerStepActive
        ? SYNC_TO_PARTNER_STEP_CONTENT.HOVER_CONTENT.active
        : isPartnerStepSuccessful
          ? SYNC_TO_PARTNER_STEP_CONTENT.HOVER_CONTENT.success
          : IsSuccessUptoProcessingStep
            ? SYNC_TO_PARTNER_STEP_CONTENT.HOVER_CONTENT.failure
            : '',
      iconBackgroundColor: getIconBgColor(
        IsSuccessUptoPartnerStep,
        IsSuccessUptoProcessingStep,
        syncToPartnerStepStatus.notCompleted,
        syncToPartnerStepStatus.active,
      ),
    },
    SYNC_TO_CLIENT: {
      currentStepStatus: isClientStepSuccessful,
      nextStepStatus: isClientStepSuccessful,
      previousStepStatus: providerWithOnlyClientFlow
        ? isProcessedStepSuccessful
        : isPartnerStepSuccessful,
      description:
        syncToClientStepStatus.success || !_.isNil(sentOn)
          ? SYNC_TO_CLIENT_STEP_CONTENT.DESCRIPTION.success
          : SYNC_TO_CLIENT_STEP_CONTENT.DESCRIPTION.failure,
      hoverContent: isClientStepActive
        ? SYNC_TO_CLIENT_STEP_CONTENT.HOVER_CONTENT.active
        : isClientStepSuccessful
          ? SYNC_TO_CLIENT_STEP_CONTENT.HOVER_CONTENT.success
          : (
            providerWithOnlyClientFlow
              ? !IsSuccessUptoProcessingStep
              : !IsSuccessUptoPartnerStep
          )
            ? ''
            : SYNC_TO_CLIENT_STEP_CONTENT.HOVER_CONTENT.failure,
      iconBackgroundColor: getIconBgColor(
        IsSuccessUptoClientStep,
        providerWithOnlyClientFlow
          ? IsSuccessUptoProcessingStep
          : IsSuccessUptoPartnerStep,
        syncToClientStepStatus.notCompleted,
        syncToClientStepStatus.active,
      ),
    },
    ALL_STEPS_STATUS: STEP_STATUS_DATA,
    providerWithOnlyClientFlow,
    providerWithOnlyPartnerFlow,
  };
  return ALL_STEPS_COMPLETE_DETAILS;
};

export enum EVDIPageAccodrionPayloadView {
  JSON = 'JSON',
  XML = 'XML',
  PARTNER_XML = 'PARTNER_XML',
}
export const VDI_TRANSACTIONS_PAGE_PAYLOAD_ACCORDION_HEADERS: Record<
  EVDIPageAccodrionPayloadView,
  string
> = {
  [EVDIPageAccodrionPayloadView.JSON]: 'JSON',
  [EVDIPageAccodrionPayloadView.XML]: 'XML',
  [EVDIPageAccodrionPayloadView.PARTNER_XML]: 'Partner XML',
};
